import {useEffect, useMemo, useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableSortLabel,
    TablePagination,
    CircularProgress,
    Box,
    Typography,
} from "@mui/material";
import {apiGet} from "../utils";

const formatMoney = (v) => {
    if (v == null) return "-";
    const rounded = Math.round(v);
    return rounded < 0 ? `-$${Math.abs(rounded).toLocaleString()}` : `$${rounded.toLocaleString()}`;
};
const formatPct = (v) => (v == null ? "-" : `${v > 0 ? "+" : ""}${v}%`);

const COLUMNS = [
    {key: "account_name", label: "Account", numeric: false},
    {key: "ship_to_count", label: "Ship-To Accounts", numeric: true},
    {key: "signal_count", label: "Signals", numeric: true},
    {key: "revenue_gap", label: "Revenue Gap", numeric: true},
    {key: "revenue_gap_pct", label: "Revenue Gap %", numeric: true},
];

const sortRows = (rows, orderBy, order) => {
    if (!orderBy) {
        return rows;
    }
    const direction = order === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        const aVal = a[orderBy];
        const bVal = b[orderBy];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === "string") {
            return aVal.localeCompare(bVal) * direction;
        }
        return (aVal - bVal) * direction;
    });
};

export const SlowingDownModal = ({open, onClose}) => {
    const [loading, setLoading] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [orderBy, setOrderBy] = useState("revenue_gap");
    const [order, setOrder] = useState("asc");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    useEffect(() => {
        if (!open) {
            return;
        }
        setLoading(true);
        setAccounts([]);
        setPage(0);
        apiGet("v2/agent/sales-performance/slowing-down-accounts", (resp) => {
            const rows = resp?.success ? (resp.data?.accounts || []) : [];
            setAccounts(rows.map((a) => ({...a, signal_count: a.signals.length})));
            setLoading(false);
        });
    }, [open]);

    const sortedAccounts = useMemo(() => sortRows(accounts, orderBy, order), [accounts, orderBy, order]);
    const pagedAccounts = useMemo(
        () => sortedAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedAccounts, page, rowsPerPage]
    );

    const handleSort = (key) => {
        if (orderBy === key) {
            setOrder(order === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(key);
            setOrder("asc");
        }
        setPage(0);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Customers Showing Signs of Slowing Down</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                        <CircularProgress size={24}/>
                    </Box>
                ) : (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{display: "block", mb: 1}}>
                            {accounts.length} customers total.
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        {COLUMNS.map((col) => (
                                            <TableCell key={col.key} align={col.numeric ? "right" : "left"}>
                                                <TableSortLabel
                                                    active={orderBy === col.key}
                                                    direction={orderBy === col.key ? order : "asc"}
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    {col.label}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                        <TableCell sx={{minWidth: 320}}>Signals Detected</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pagedAccounts.map((a, i) => (
                                        <TableRow key={a.account_id}>
                                            <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                                            <TableCell>{a.account_name}</TableCell>
                                            <TableCell align="right">{a.ship_to_count}</TableCell>
                                            <TableCell align="right">{a.signal_count}</TableCell>
                                            <TableCell align="right">{formatMoney(a.revenue_gap)}</TableCell>
                                            <TableCell align="right">{formatPct(a.revenue_gap_pct)}</TableCell>
                                            <TableCell sx={{whiteSpace: "pre-line"}}>{(a.signal_details || []).join("\n")}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!pagedAccounts.length && (
                                        <TableRow>
                                            <TableCell colSpan={COLUMNS.length + 2}>No accounts found.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {accounts.length > 0 && (
                            <TablePagination
                                component="div"
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                count={accounts.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
